import { useState, useEffect, useCallback, useRef } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { isEmptyObject, getFilterDataFromURL, getRelatedTo } from '#component/general/list-filter/helper';
import { setInitialState, setFilterData as originSetFilterData, setFilterAppliedStatus } from '#action/list-filter';
import { useUpdateURL } from './useUpdateURLParams';
import { LocalStorageService } from '#service';
import { requestStatuses as statuses } from '#helper';

// Get filter based on LS or URL
const getFilterData = async () => {
  let filterData;
  const searchParams = window.location.search.length;

  if (searchParams) {
    filterData = await getFilterDataFromURL();
  } else {
    filterData = LocalStorageService.getItem('listFilter');
  }

  return filterData;
};

const useApplyFilter = ({ setFilterData, clearSourceFilterData }) => {
  const dispatch = useDispatch();
  const relatedTo = getRelatedTo();
  const changeURL = useUpdateURL();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const filter = useSelector(s => s.listFilter, shallowEqual);
  const localStorageFilter = LocalStorageService.getItem('listFilter');

  const applyFilter = async () => {
    const data = await getFilterData();
    dispatch(setFilterData(data));

    const currentLocalStorageFilter = LocalStorageService.getItem('listFilter');
    const newFilter = {
      ...currentLocalStorageFilter,
      ...data
    };
    LocalStorageService.setItem('listFilter', newFilter);

    setResult(data);
    setLoading(false);
  };

  const applyFilterToURL = async () => {
    const getFilter = () => {
      if (!isEmptyObject(filter?.[relatedTo])) {
        return filter;
      }

      if (!isEmptyObject(localStorageFilter?.[relatedTo])) {
        return localStorageFilter;
      }

      return undefined;
    };

    const savedFilter = getFilter();

    if (savedFilter) {
      await changeURL(savedFilter);
      setResult(savedFilter);
      setLoading(false);
    }
  };

  const applyNonSyncFilter = async () => {
    const searchParams = window.location.search.length;
    dispatch(clearSourceFilterData(relatedTo));

    if (searchParams) {
      const data = await getFilterDataFromURL();
      dispatch(setFilterData(data));
      setResult(data);
      setLoading(false);
    } else {
      setResult([]);
      setLoading(false);
    }
  };

  return { applyFilter, applyNonSyncFilter, applyFilterToURL, result, loading };
};

export const useSyncFilter = (actions, disableSync) => {
  const dispatch = useDispatch();
  const { setFilterStatus } = actions;
  const didSync = useRef(false);
  const relatedTo = getRelatedTo();
  const filterState = useSelector(s => s.listFilter, shallowEqual);
  const filter = filterState?.[relatedTo];
  const localStorageFilter = LocalStorageService.getItem('listFilter');
  const searchParamsLength = window.location.search.length;
  const isEmptyFilter = isEmptyObject(filter);
  const isEmptyLSFilter = isEmptyObject(localStorageFilter?.[relatedTo]);
  const shouldSync = (isEmptyFilter && !isEmptyLSFilter) || (isEmptyFilter && searchParamsLength);

  const {
    result,
    loading,
    applyFilter,
    applyFilterToURL,
    applyNonSyncFilter
  } = useApplyFilter(actions);

  useEffect(() => {
    if (!didSync.current) {
      didSync.current = true;
      dispatch(setFilterStatus(statuses.loading));

      const syncData = async () => {
        // Update filter state
        if (shouldSync) {
          applyFilter();
          return;
        }

        // Update URL state
        if (!isEmptyObject(filter) && !searchParamsLength) {
          applyFilterToURL();
          return;
        }

        dispatch(setFilterStatus(statuses.succeeded));
      };

      const syncTrailsData = async () => {
        applyNonSyncFilter();
      };

      if (!disableSync) {
        syncData();
      } else {
        syncTrailsData();
      }

      dispatch(setFilterAppliedStatus(true));
    }
  }, [
    applyFilter, applyFilterToURL, applyNonSyncFilter, shouldSync,
    disableSync, dispatch, filter, searchParamsLength, setFilterStatus
  ]);

  useEffect(() => {
    if (result && !loading) {
      dispatch(setFilterStatus(statuses.succeeded));
      dispatch(setInitialState(result));
    }
  }, [dispatch, loading, result, setFilterStatus]);
};

export const useResetToAppliedFilter = () => {
  const dispatch = useDispatch();
  const relatedTo = getRelatedTo();
  const filter = useSelector(s => s.listFilter, shallowEqual);
  const currentAppliedFilter = useSelector(s => s.listFilter?.initial?.[relatedTo], shallowEqual);

  return useCallback(() => {
    let newFilter = {};
    const { search, ...rest } = currentAppliedFilter || {}; // Without search field

    if (Object.keys(filter || {}).length) {
      newFilter = {
        ...filter,
        ...((search || currentAppliedFilter) && {
          [relatedTo]: {
            ...rest,
            ...(filter[relatedTo]?.search && { search: filter[relatedTo]?.search })
          }
        })
      };
    } else {
      newFilter[relatedTo] = rest;
    }

    dispatch(originSetFilterData(newFilter));
  }, [currentAppliedFilter, dispatch, filter, relatedTo]);
};
